import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import WorldMap from "@/components/WorldMap";
import { http } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";

const SEVERITY_COLOR = {
  low: "text-emerald-300",
  moderate: "text-yellow-300",
  high: "text-orange-400",
  severe: "text-[color:var(--np-alert)]",
};

const HAZARDS = ["flood","wildfire","heatwave","cyclone","landslide","drought","air-quality"];

export default function CivicReports() {
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ hazard: "flood", severity: "moderate", location: "", description: "", reporter_name: "" });
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/civic-reports").then(r => setReports(r.data.reports || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/civic-reports", form);
      toast.success("Report filed. Thank you for the eyes.");
      setForm({ ...form, location: "", description: "", reporter_name: "" });
      load();
    } catch { toast.error("Could not file report."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHero
        eyebrow="/ Civic Reports"
        title="Communities as first responders."
        subtitle="A public ledger of active hazards — floods, wildfires, heat waves, cyclones, landslides, drought, and air-quality events, filed by neighbors and agencies."
        image="https://images.pexels.com/photos/7913319/pexels-photo-7913319.jpeg"
      />

      <WorldMap />


      <section className="py-24 md:py-32 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/02 · Active reports</div>
            <div className="border border-black/10 divide-y divide-black/10">
              {reports.map((r, i) => (
                <motion.div
                  key={r.report_id}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.3, delay: i * 0.02 }}
                  className="p-6 md:p-8 grid grid-cols-12 gap-4 items-baseline hover:bg-black/5 transition-colors"
                  data-testid={`civic-report-${i}`}
                >
                  <div className="col-span-3 md:col-span-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
                    {new Date(r.created_at).toISOString().slice(5,10)}
                  </div>
                  <div className={`col-span-3 md:col-span-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase ${SEVERITY_COLOR[r.severity] || ""}`}>
                    {r.severity}
                  </div>
                  <div className="col-span-6 md:col-span-3 font-serif-display text-2xl leading-tight tracking-tight capitalize">{r.hazard.replace("-"," ")}</div>
                  <div className="col-span-12 md:col-span-5 text-[color:var(--np-text-2)]">
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase mb-1">{r.location} · {r.reporter_name}</div>
                    <div>{r.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="glass p-6 sticky top-32">
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-live)] mb-4">/02 · File a report</div>
              <h3 className="font-serif-display text-3xl mb-6 tracking-tight">See something. Say something.</h3>
              <form onSubmit={submit} className="space-y-4" data-testid="civic-report-form">
                <select value={form.hazard} onChange={(e) => setForm({ ...form, hazard: e.target.value })} className="w-full bg-[color:var(--np-bg)] border border-black/15 px-3 py-3 font-mono-tech text-xs uppercase tracking-widest" data-testid="civic-hazard-select">
                  {HAZARDS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full bg-[color:var(--np-bg)] border border-black/15 px-3 py-3 font-mono-tech text-xs uppercase tracking-widest" data-testid="civic-severity-select">
                  {["low","moderate","high","severe"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input required value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Location" className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-sm" data-testid="civic-location-input" />
                <input required value={form.reporter_name} onChange={e => setForm({ ...form, reporter_name: e.target.value })} placeholder="Your name / agency" className="w-full bg-transparent border-b border-black/15 focus:border-[color:var(--np-live)] outline-none py-2 font-mono-tech text-sm" data-testid="civic-reporter-input" />
                <textarea required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What did you see?" rows={4} className="w-full bg-transparent border border-black/15 focus:border-[color:var(--np-live)] outline-none p-3 text-sm resize-none" data-testid="civic-description-input" />
                <button disabled={busy} className="w-full font-mono-tech text-[11px] tracking-[0.3em] uppercase border border-black/15 hover:bg-[color:var(--np-text)] hover:text-[color:var(--np-bg)] px-6 py-3 transition-colors" data-testid="civic-submit-btn">
                  {busy ? "Filing…" : "File report"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
